import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Spinner,
  Badge,
  InputGroup,
  Modal,
} from "react-bootstrap";
import Pagination from "../components/Pagination";
import moment from "moment";
import { toast } from "react-toastify";
import {
  getTickets,
  getTicketById,
  updateTicket,
  addTicketMessage,
  getTicketAssignees,
} from "../context/functions/AdminFunction";
import {
  FiPhone,
  FiMail,
  FiSearch,
  FiX,
  FiRefreshCw,
  FiChevronUp,
  FiChevronDown,
  FiInfo,
} from "react-icons/fi";
import "../styles/llm-cost-analysis.css";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Open", label: "Open" },
  { value: "In progress", label: "In progress" },
  { value: "Waiting on user", label: "Waiting on user" },
  { value: "Resolved", label: "Resolved" },
  { value: "Closed", label: "Closed" },
];

const PRIORITY_OPTIONS = [
  { value: "", label: "All Urgency" },
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "account_access", label: "Account access" },
  { value: "document_issue", label: "Document issue" },
  { value: "billing", label: "Billing" },
  { value: "fraud", label: "Fraud" },
  { value: "gdpr", label: "GDPR" },
  { value: "integration_api", label: "Integration / API" },
  { value: "product_question", label: "Product question" },
  { value: "other", label: "Other" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "web", label: "Web" },
  { value: "email", label: "Email" },
  { value: "voice", label: "Voice" },
  { value: "api", label: "API" },
];

const STATUS_BADGE = {
  Open: "primary",
  "In progress": "warning",
  "Waiting on user": "info",
  Resolved: "success",
  Closed: "secondary",
};

const PRIORITY_BADGE = {
  High: "danger",
  Medium: "warning",
  Low: "success",
};

const USER_TYPE_LABEL = {
  tenant: "Tenant",
  guarantor: "Guarantor",
  landlord: "Landlord",
  agency: "Agency",
  unknown: "Unknown",
};

const SortHeader = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {active ? (
          sortOrder === "desc" ? (
            <FiChevronDown size={12} />
          ) : (
            <FiChevronUp size={12} />
          )
        ) : (
          <FiChevronDown size={12} style={{ opacity: 0.3 }} />
        )}
      </span>
    </th>
  );
};

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 20;

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Detail modal
  const [showModal, setShowModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { ticket, timeline }
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState({
    status: "",
    priority: "",
    assignee: "",
    resolution_notes: "",
  });
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    document.title = "Support Tickets | Admin";
    getTicketAssignees().then((res) => {
      if (res?.success) setAssignees(res.data || []);
    });
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const fetchData = useCallback(
    async (currentPage = 1, signal = null) => {
      setLoading(true);
      try {
        const filters = {
          search: debouncedSearch,
          status,
          priority,
          category,
          source,
          sortBy,
          sortOrder,
        };
        const res = await getTickets(filters, currentPage, limit, signal);
        if (res?.status === "canceled") return;
        if (res && res.success) {
          setTickets(res.data.tickets || []);
          setStats(res.data.stats || null);
          setTotalPages(res.data.pagination?.totalPages || 1);
          setTotalRecords(res.data.pagination?.totalRecords || 0);
        } else {
          setTickets([]);
          setStats(null);
          setTotalPages(1);
          setTotalRecords(0);
        }
      } catch (e) {
        console.error(e);
        setTickets([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, status, priority, category, source, sortBy, sortOrder]
  );

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchData(page, controller.signal);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchData, page]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handlePageClick = (e) => setPage(e.selected + 1);

  const handleReset = () => {
    setSearch("");
    setStatus("");
    setPriority("");
    setCategory("");
    setSource("");
    setSortBy("created_at");
    setSortOrder("desc");
    setPage(1);
  };

  const openDetail = async (ticket) => {
    setShowModal(true);
    setDetail(null);
    setDetailLoading(true);
    setNote("");
    const res = await getTicketById(ticket._id);
    setDetailLoading(false);
    if (res?.success) {
      setDetail(res.data);
      const t = res.data.ticket;
      setForm({
        status: t.status || "",
        priority: t.priority || "",
        assignee: t.assignee?._id || t.assignee || "",
        resolution_notes: t.resolution_notes || "",
      });
    } else {
      toast.error("Failed to load ticket");
    }
  };

  const handleSave = async () => {
    if (!detail?.ticket?._id) return;
    setSaving(true);
    try {
      const res = await updateTicket(detail.ticket._id, {
        status: form.status,
        priority: form.priority,
        assignee: form.assignee || null,
        resolution_notes: form.resolution_notes,
      });
      if (res?.success) {
        toast.success("Ticket updated");
        setShowModal(false);
        fetchData(page);
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async (visibility) => {
    if (!note.trim() || !detail?.ticket?._id) return;
    try {
      const res = await addTicketMessage(detail.ticket._id, {
        body: note.trim(),
        visibility,
      });
      if (res?.success) {
        setDetail((d) => ({
          ...d,
          timeline: [...(d.timeline || []), res.data],
        }));
        setNote("");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add note");
    }
  };

  const badge = (value, map) => (
    <Badge bg={map[value] || "secondary"} style={{ fontSize: 11, fontWeight: 700 }}>
      {value || "—"}
    </Badge>
  );

  return (
    <div className="llm-cost-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4
            className="fw-800 mb-1"
            style={{ color: "var(--llm-color-text-primary)", fontSize: "22px" }}
          >
            Support Tickets
          </h4>
          <p className="text-muted small fw-medium mb-0">
            Incidents and support requests across every channel.
          </p>
        </div>
        <Button
          variant="white"
          className="border shadow-none d-flex align-items-center gap-2"
          style={{ fontSize: "13px", fontWeight: 700, borderRadius: "8px" }}
          onClick={() => fetchData(page)}
        >
          <FiRefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="d-flex gap-3 mb-4 flex-wrap">
          {[
            { label: "Open", value: stats.open, color: "#3b82f6" },
            { label: "In progress", value: stats.in_progress, color: "#f59e0b" },
            { label: "Waiting", value: stats.waiting_on_user, color: "#06b6d4" },
            { label: "Resolved", value: stats.resolved, color: "#22c55e" },
            { label: "Closed", value: stats.closed, color: "#94a3b8" },
          ].map((s) => (
            <div
              key={s.label}
              style={{
                background: "#fff",
                border: "1px solid #e8edf2",
                borderRadius: 12,
                padding: "14px 20px",
                flex: 1,
                minWidth: 120,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                {s.label}
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>
                {(s.value || 0).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Body className="p-3">
          <div className="llm-filter-row" style={{ flexWrap: "wrap", gap: 10 }}>
            <div className="llm-filter-item" style={{ flex: 2, minWidth: 200 }}>
              <span className="llm-filter-label">Search</span>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0">
                  <FiSearch size={13} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  className="border-start-0 ps-1"
                  style={{ height: "38px" }}
                  type="text"
                  placeholder="Email, phone or ticket ref..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <InputGroup.Text
                    className="bg-white border-start-0"
                    onClick={() => setSearch("")}
                    style={{ cursor: "pointer" }}
                  >
                    <FiX size={13} className="text-danger" />
                  </InputGroup.Text>
                )}
              </InputGroup>
            </div>

            {[
              { label: "Status", val: status, set: setStatus, opts: STATUS_OPTIONS },
              { label: "Urgency", val: priority, set: setPriority, opts: PRIORITY_OPTIONS },
              { label: "Category", val: category, set: setCategory, opts: CATEGORY_OPTIONS },
              { label: "Source", val: source, set: setSource, opts: SOURCE_OPTIONS },
            ].map((f) => (
              <div className="llm-filter-item" key={f.label}>
                <span className="llm-filter-label">{f.label}</span>
                <Form.Select
                  size="sm"
                  value={f.val}
                  onChange={(e) => {
                    f.set(e.target.value);
                    setPage(1);
                  }}
                  style={{ height: "38px", borderRadius: "6px" }}
                >
                  {f.opts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            ))}

            <Button
              className="llm-btn-apply"
              onClick={handleReset}
              style={{ background: "#f1f5f9", color: "#253837", border: "1px solid #e2e8f0" }}
            >
              Reset
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
          <h6
            className="mb-0 fw-800 text-uppercase"
            style={{ fontSize: "13px", letterSpacing: "0.05em" }}
          >
            Tickets
          </h6>
          <span className="text-muted small fw-medium" style={{ fontSize: "11px" }}>
            {totalRecords.toLocaleString()} total records
          </span>
        </Card.Header>
        <div className="table-responsive">
          <Table className="llm-table" hover>
            <thead>
              <tr>
                <SortHeader label="Created" field="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <th>Ref</th>
                <th>Type</th>
                <th>Contact</th>
                <th>Summary</th>
                <th>Urgency</th>
                <th>Status</th>
                <th className="text-center">Retries</th>
                <th>Assignee</th>
                <th className="text-center">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <Spinner size="sm" animation="border" variant="primary" />
                    <p className="mt-2 text-muted small fw-bold text-uppercase mb-0">
                      Loading...
                    </p>
                  </td>
                </tr>
              ) : tickets.length > 0 ? (
                tickets.map((t) => (
                  <tr key={t._id} style={{ cursor: "pointer" }} onClick={() => openDetail(t)}>
                    <td>
                      <span className="fw-bold d-block text-dark" style={{ fontSize: "13px" }}>
                        {moment(t.created_at).format("DD MMM YYYY")}
                      </span>
                      <small className="text-muted fw-medium" style={{ fontSize: "11px" }}>
                        {moment(t.created_at).format("HH:mm")}
                      </small>
                    </td>
                    <td>
                      <span className="fw-bold" style={{ fontSize: "12px" }}>
                        {t.ticket_ref}
                      </span>
                      {(t.tags || []).includes("no-account") && (
                        <Badge bg="light" text="dark" className="ms-1 border" style={{ fontSize: 9 }}>
                          no-account
                        </Badge>
                      )}
                    </td>
                    <td>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                        {USER_TYPE_LABEL[t.requester?.user_type] || "—"}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: "12px" }}>
                        {t.requester?.phone && (
                          <div className="text-muted d-flex align-items-center gap-1">
                            <FiPhone size={10} /> {t.requester.phone}
                          </div>
                        )}
                        {t.requester?.email ? (
                          <div className="text-muted d-flex align-items-center gap-1">
                            <FiMail size={10} /> {t.requester.email}
                          </div>
                        ) : (
                          <div className="text-muted fst-italic">no email</div>
                        )}
                      </div>
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      <span
                        style={{
                          fontSize: 12,
                          color: "#475569",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {t.description || "—"}
                      </span>
                    </td>
                    <td>{badge(t.priority, PRIORITY_BADGE)}</td>
                    <td>{badge(t.status, STATUS_BADGE)}</td>
                    <td className="text-center fw-700" style={{ color: t.contact_attempts > 0 ? "#dc2626" : "#94a3b8" }}>
                      {t.contact_attempts || 0}
                    </td>
                    <td>
                      <span style={{ fontSize: 12, color: "#475569" }}>
                        {t.assignee?.name || t.assignee?.email || "—"}
                      </span>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="white"
                        className="border shadow-none py-1 px-2 d-inline-flex align-items-center gap-1"
                        style={{ fontSize: "11px", borderRadius: "6px", fontWeight: 700 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetail(t);
                        }}
                      >
                        <FiInfo size={12} /> View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-5 text-muted">
                    <FiSearch size={32} className="mb-2 opacity-25 d-block mx-auto" />
                    <span className="fw-bold">No tickets found</span>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {!loading && tickets.length > 0 && (
          <div className="d-flex justify-content-end p-3 border-top bg-light bg-opacity-50">
            <Pagination
              currentPage={page - 1}
              totalPages={totalPages}
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              className="pagination mb-0"
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <Modal.Title className="fw-bold h5">
            {detail?.ticket?.ticket_ref || "Ticket"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {detailLoading || !detail ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          ) : (
            <div>
              {/* Info grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                {[
                  { label: "Type", value: USER_TYPE_LABEL[detail.ticket.requester?.user_type] || "—" },
                  { label: "Source", value: detail.ticket.source || "—" },
                  { label: "Category", value: detail.ticket.category || "—" },
                  { label: "Name", value: detail.ticket.requester?.name || "—" },
                  { label: "Email", value: detail.ticket.requester?.email || "No facilitado" },
                  { label: "Phone", value: detail.ticket.requester?.phone || "—" },
                  { label: "Created", value: moment(detail.ticket.created_at).format("DD MMM YYYY, HH:mm") },
                  { label: "Retries", value: detail.ticket.contact_attempts || 0 },
                  {
                    label: "Linked account",
                    value: detail.ticket.requester?.user_ref ? "Yes" : "No",
                  },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-light p-3 rounded-3 border">
                    <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px" }}>
                      {label}
                    </p>
                    <div className="fw-bold text-dark" style={{ fontSize: "13px", wordBreak: "break-word" }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mb-3">
                <p className="text-muted small mb-2 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                  Summary
                </p>
                <div className="p-3 bg-light rounded-3 border" style={{ fontSize: 13, lineHeight: 1.6, color: "#334155" }}>
                  {detail.ticket.description || "—"}
                </div>
              </div>

              {/* Related tickets */}
              {detail.ticket.related_tickets?.length > 0 && (
                <div className="mb-3">
                  <p className="text-muted small mb-2 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                    Linked previous tickets
                  </p>
                  {detail.ticket.related_tickets.map((r) => (
                    <Badge key={r._id} bg="light" text="dark" className="border me-1">
                      {r.ticket_ref} · {r.status}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Edit controls */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: 10 }}>
                    Status
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: 10 }}>
                    Urgency
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-md-4">
                  <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: 10 }}>
                    Assignee
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={form.assignee}
                    onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                  >
                    <option value="">Unassigned</option>
                    {assignees.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.name || a.email}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-12">
                  <Form.Label className="small fw-bold text-uppercase text-muted" style={{ fontSize: 10 }}>
                    Resolution notes
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={form.resolution_notes}
                    onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })}
                  />
                </div>
              </div>

              {/* Timeline */}
              <p className="text-muted small mb-2 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                Timeline
              </p>
              <div style={{ maxHeight: 200, overflowY: "auto" }} className="mb-3">
                {(detail.timeline || []).map((m) => (
                  <div key={m._id} className="d-flex gap-2 mb-2" style={{ fontSize: 12 }}>
                    <div style={{ minWidth: 90 }} className="text-muted">
                      {moment(m.created_at).format("DD MMM HH:mm")}
                    </div>
                    <div>
                      <Badge
                        bg={m.visibility === "internal" ? "secondary" : "primary"}
                        style={{ fontSize: 9, marginRight: 6 }}
                      >
                        {m.author?.type || "system"}
                      </Badge>
                      {m.body}
                    </div>
                  </div>
                ))}
                {(!detail.timeline || detail.timeline.length === 0) && (
                  <div className="text-muted small">No activity yet.</div>
                )}
              </div>

              {/* Add note / reply */}
              <InputGroup size="sm" className="mb-2">
                <Form.Control
                  placeholder="Add an internal note or public reply..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <Button variant="outline-secondary" onClick={() => handleAddNote("internal")}>
                  Note
                </Button>
                <Button variant="outline-primary" onClick={() => handleAddNote("public")}>
                  Reply
                </Button>
              </InputGroup>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 pb-4">
          <Button variant="light" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button
            style={{ background: "#253837", border: "none" }}
            onClick={handleSave}
            disabled={saving || detailLoading || !detail}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .pagination .page-item .page-link {
          color: var(--llm-color-text-secondary);
          border: 1px solid var(--llm-color-border);
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .pagination .page-item.active .page-link {
          background-color: var(--llm-color-primary) !important;
          border-color: var(--llm-color-primary) !important;
          color: var(--llm-color-dark) !important;
        }
      `}</style>
    </div>
  );
};

export default Tickets;
